// Every user-facing string lives here (CLAUDE.md §3.8). Keys are grouped by screen.
export const en = {
  app: {
    name: 'FamilyWallet',
    tagline: 'Your family finances, together.',
    loading: 'Loading…',
    updateReady: 'A new version of FamilyWallet is ready.',
    updateNow: 'Update',
  },
  nav: {
    label: 'Main',
    home: 'Home',
    activity: 'Activity',
    add: 'Add',
    accounts: 'Accounts',
    members: 'Members',
    family: 'Family',
    account: 'Account',
  },
  common: {
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    back: 'Back',
    saved: 'Saved',
    copy: 'Copy link',
    copied: 'Copied',
    share: 'Share',
    retry: 'Try again',
    close: 'Close',
    loadFailed: 'Something went wrong while loading. Check your connection and try again.',
  },
  roles: {
    owner: 'Owner',
    parent: 'Parent',
    member: 'Member',
    child: 'Child',
  },
  roleHints: {
    parent: "Can manage the family and see every member's accounts",
    member: 'Manages their own accounts; parents can see them',
    child: 'Has their own accounts; parents can see and manage them',
  },
  permissions: {
    'family.view': 'See family settings',
    'family.manage': 'Change family settings',
    'members.view': 'See family members',
    'members.manage': 'Add, edit and deactivate members',
    'members.invite': 'Invite members to sign in',
    'permissions.manage': 'Change permissions',
    'accounts.manage_own': 'Create and manage own accounts',
    'accounts.manage_shared': 'Create and manage shared family accounts',
    'accounts.manage_others': 'Manage accounts of children and members without a login',
    'accounts.private': 'Mark own accounts as private (hidden from everyone else)',
    'categories.manage': 'Add, rename and archive categories',
    'payees.manage': 'Rename, merge and archive payees',
    'transactions.record_own': 'Record money on own accounts',
    'transactions.record_shared': 'Record money on shared family accounts',
    'transactions.record_others': "Record money on other members' accounts",
    'transactions.reverse_own': 'Correct own transactions',
    'transactions.reverse_any': "Correct anyone's transactions",
    'rates.manage': 'Add exchange rates',
    'debts.outside': 'Record own debts with people outside the family',
    'debts.family': 'Record own debts with family members',
    'debts.on_behalf': 'Record debts on behalf of another member',
    'debts.plans': 'Create and change installment plans on own debts',
    'people.manage': 'Rename, merge and archive people',
    'budgets.view': 'See the family budgets',
    'budgets.manage': 'Set the family budgets',
    'limits.manage': "Set children's monthly limits",
    'reports.view': 'See family reports (without it: own spending only; never for a child)',
  },
  fields: {
    email: 'Email',
    password: 'Password',
    newPassword: 'Choose a password',
    passwordHint: 'At least 10 characters.',
    displayName: 'Your name',
    memberName: 'Name',
    familyName: 'Family name',
    role: 'Role',
    baseCurrency: 'Main currency',
    baseCurrencyHint: 'Three letters, for example USD, LBP or EUR.',
    timezone: 'Time zone',
    timezoneHint: 'Used to group spending by day, for example Asia/Beirut.',
  },
  validation: {
    required: 'This field is required',
    email: 'Enter a valid email address',
    passwordLength: 'Password must be at least 10 characters',
    passwordMismatch: 'The two passwords do not match',
    tooLong: 'This is too long',
    currency: 'Use 3 capital letters, for example USD',
    timezone: 'Unknown time zone',
    invalid: 'Check this field',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    'recurring.not_found': 'This recurring transaction no longer exists.',
    'recurring.already_recorded': 'This occurrence was already recorded or skipped.',
    'recurring.not_due': 'Nothing is due on this day.',
    'recurring.kind_mismatch': 'This occurrence is a different kind of transaction.',
    'goal.not_found': 'This goal does not exist or is not visible to you.',
    network: 'Cannot reach the server. Check your connection.',
    'auth.invalid_credentials': 'Email or password is incorrect.',
    'auth.wrong_current_password': 'Your current password is incorrect.',
    'auth.account_deactivated': 'This account has been deactivated. Ask a parent in your family.',
    'auth.invalid_token': 'Your session has expired. Please sign in again.',
    'auth.refresh_reused': 'For your security you were signed out. Please sign in again.',
    'auth.registration_disabled': 'Sign-up is closed. Ask your family owner for an invitation.',
    'auth.email_taken': 'An account with this email already exists. Sign in instead.',
    'auth.already_in_family': 'This account already belongs to a family.',
    'auth.owner_must_transfer':
      'Other family members can still sign in. Remove their access before deleting your account.',
    'invitation.not_found': 'This invitation link is not valid.',
    'invitation.expired': 'This invitation has expired. Ask for a new one.',
    'invitation.already_used': 'This invitation was already used. Try signing in.',
    'invitation.revoked': 'This invitation was cancelled. Ask for a new one.',
    'invitation.member_has_login': 'This person can already sign in.',
    'member.not_found': 'This member does not exist.',
    'member.forbidden_change': 'You are not allowed to make this change.',
    'auth.family_name_mismatch': 'Type the family name exactly as it appears to confirm.',
    'family.currency_locked': 'The family currency cannot be changed after the first transaction.',
    'account.not_found': 'This account does not exist or is not visible to you.',
    'account.forbidden': 'You are not allowed to change this account.',
    'account.private_not_allowed': 'You are not allowed to make accounts private.',
    'account.balance_not_zero': 'This account still has money in it.',
    'account.archived': 'This account is archived.',
    'account.currency_exists': 'This account already holds that currency.',
    'account.currency_not_found': 'This account does not hold that currency.',
    'account.last_currency': 'An account needs at least one currency. Archive the account instead.',
    'account.currency_balance_not_zero':
      'Move or spend that money first: a currency can be removed only when its balance is zero.',
    'currency.not_enabled': 'Your family does not use this currency.',
    'amount.too_precise': 'This amount has too many decimals for its currency.',
    'rate.forbidden': 'Only the owner and parents can add exchange rates.',
    'rate.future': 'A rate cannot start in the future.',
    'rate.same_currency': 'A rate converts another currency to the family currency.',
    'rate.required': 'There was no family rate yet on that date. Enter a rate for this one.',
    'category.not_found': 'This category does not exist.',
    'category.archived': 'This category is archived. Pick another one.',
    'account.currency_missing':
      'This account does not hold that currency. Add the currency to the account first.',
    'transaction.future_date': 'The date cannot be in the future.',
    'transaction.share_needs_private':
      'Only a payment from a private account can be shared with the family.',
    'transfer.same_account': 'Pick two different accounts.',
    'rate.not_needed': 'The family currency needs no rate.',
    'payee.not_found': 'This payee does not exist.',
    'idempotency.key_reused': 'This was already saved with different details. Try again.',
    'category.name_taken': 'A category with this name already exists.',
    'idempotency.in_progress': 'This is still being saved. Wait a moment.',
    'debt.not_found': 'This debt does not exist or is not visible to you.',
    'debt.forbidden': 'You are not allowed to record this debt.',
    'debt.overpayment': 'This is more than what is outstanding on this debt.',
    'debt.interest_not_allowed':
      'Interest and fees can only be split from a repayment to someone outside the family.',
    'debt.has_payments':
      'Payments on this debt still stand. Correct those payments first, then the debt.',
    'debt.self': 'A member cannot owe themselves.',
    'credit.less_than_paid':
      'More than this was already paid on the credit. Correct those payments first, or keep more on credit.',
    'debt.other_currency_not_allowed': 'A loan is repaid in its own currency.',
    'debt.payment_too_small': 'This is less than the smallest amount in the other currency.',
    'transaction.kind_mismatch': 'Correct it with the same kind of transaction.',
    'installment.not_open': 'This installment is already paid or skipped.',
    'plan.start_required': 'Pick the month of the first installment.',
    'plan.start_too_early': 'A change cannot move installments earlier than the next one due.',
    'plan.debt_settled': 'This debt is already paid off.',
    'member.inactive': 'This member is deactivated.',
    'person.not_found': 'This person does not exist.',
    'person.archived': 'This person is archived. Bring them back first.',
    'person.name_taken': 'Someone with this name is already in the list.',
    'person.merge_self': 'Pick a different person to merge into.',
    'foreign_expense.amounts_mismatch':
      'Bill, paid and returned do not fit together. Check the amounts and currencies.',
    'foreign_expense.nothing_spent':
      'The change is as much as what was paid, so nothing was spent.',
    'foreign_expense.too_many_currencies': 'Use at most two currencies.',
    'foreign_expense.split_mismatch': 'The split lines must add up to what was spent.',
    'payee.name_taken': 'Another payee has this name. Merge them instead.',
    'payee.merge_self': 'Pick a different payee to merge into.',
    'transaction.not_found': 'This transaction does not exist or is not visible to you.',
    'transaction.forbidden': 'You are not allowed to correct this transaction.',
    'transaction.already_reversed': 'This transaction was already corrected.',
    'transaction.is_reversal':
      'A correction cannot be corrected. Record the transaction again instead.',
    forbidden: 'You are not allowed to do this.',
    rate_limited: 'Too many attempts. Wait a minute and try again.',
    validation_failed: 'Some fields are not valid.',
  },
  login: {
    title: 'Sign in',
    submit: 'Sign in',
    submitting: 'Signing in…',
    noAccount: 'New here?',
    register: 'Create a family',
  },
  register: {
    title: 'Create your family',
    intro: 'You will be the owner. You can add your family members next.',
    submit: 'Create family',
    submitting: 'Creating…',
    haveAccount: 'Already have an account?',
    login: 'Sign in',
  },
  acceptInvite: {
    title: 'Join {{family}}',
    intro: '{{inviter}} invited you to join as {{name}}.',
    introNoInviter: 'You were invited to join as {{name}}.',
    existingAccount: 'You already have an account with this email. Enter its password to join.',
    newAccount: 'Choose a password to create your account.',
    submit: 'Join family',
    submitting: 'Joining…',
    goToLogin: 'Go to sign in',
  },
  home: {
    greeting: 'Hello, {{name}}',
    familyLine: '{{family}} · {{role}}',
    totalTitle: 'Total you can see',
    recent: 'Recent',
    openReports: 'Open reports for this month',
    seeAll: 'See all',
    emptyRecent: 'Nothing recorded yet. Add your first expense or income.',
    saved: 'Saved.',
    income: 'Income',
    expenses: 'Expenses',
    spentOfIncome: '{{percent}}% of income spent',
    spentBar: 'Share of income spent this month',
    netWorth: 'Net worth',
    netWorthHint: 'Money you can see, plus what is owed to us, minus what we owe',
    debtsTitle: 'Debts',
    dueSoon: 'Due soon',
    dueSoonHint: 'Installments and recurring transactions due in the next 7 days, and overdue ones',
    nothingDue: 'Nothing due in the next 7 days.',
  },
  add: {
    title: 'Add',
    kindLabel: 'What to record',
    kinds: { expense: 'Expense', income: 'Income', transfer: 'Transfer', exchange: 'Exchange' },
    amount: 'Amount',
    currency: 'Currency',
    fastEntry: 'Type 500k for 500,000 or 1.5m for 1,500,000',
    otherRate: 'Use another rate',
    oneOffHint: 'For this transaction only, for example the exchange shop rate.',
    payee: 'Paid to',
    payer: 'From',
    payeePlaceholder: 'Shop or person',
    payeeSuggestions: 'Suggestions',
    category: 'Category',
    pickCategory: 'Choose category',
    createCategory: 'Create "{{name}}"',
    askParentCategory: 'Not in the list? Ask a parent to add this category.',
    account: 'Account',
    pickAccount: 'Choose account',
    pickTo: 'Choose where it goes',
    swap: 'Swap From and To',
    from: 'From',
    to: 'To',
    date: 'Date',
    note: 'Note',
    notePlaceholder: 'Optional',
    split: 'Split into several categories',
    unsplit: 'Use one category',
    splitTitle: 'Split',
    addLine: 'Add line',
    removeLine: 'Remove line {{n}}',
    lineAmount: 'Amount for line {{n}}',
    lineNote: 'Note for line {{n}}',
    lineNotePlaceholder: 'Note for this line (optional)',
    remaining: 'Remaining: {{amount}}',
    splitBalanced: 'The lines add up to the total',
    share: 'Share with family',
    shareHint:
      'Everyone sees this payment, its amount and category. Your private account stays hidden.',
    save: 'Save',
    noAccounts: 'There is no account you can record on yet.',
    noAccountsAction: 'Create an account',
    noCommonCurrency: 'These two accounts hold no currency in common.',
    errors: {
      amountRequired: 'Enter an amount',
      amount: 'Enter an amount like 12.50',
      amountWhole: '{{currency}} amounts are whole numbers, like 500,000 or 500k',
      category: 'Choose a category',
      account: 'Choose an account',
      sameAccount: 'Pick two different accounts',
      remaining: 'The lines must add up to the total',
      rate: 'Enter a rate like 89,500',
      date: 'Choose a date',
    },
  },
  exchange: {
    give: 'You give',
    get: 'You get',
    fromAccount: 'From account',
    toAccount: 'To account',
    dealRate: 'Rate: 1 {{base}} = {{rate}} {{quote}}',
    familyRate: 'family rate {{rate}}',
    noOtherCurrency:
      'The chosen account holds no other currency. Pick another account or add a currency to it.',
    hint: 'An exchange is not spending: the money just changes currency.',
    errors: { sameCurrency: 'Pick two different currencies' },
  },
  credit: {
    toggle: 'Part on credit',
    toggleHintExpense: 'Pay part now (or nothing) and owe the rest to the shop or person.',
    toggleHintIncome: 'Receive part now (or nothing); the rest is owed to you.',
    paidNow: 'Paid now',
    receivedNow: 'Received now',
    onCredit: 'On credit',
    owedTo: 'Owed to',
    owedBy: 'Owed by',
    dueOn: 'Due date',
    hintExpense:
      'The whole total counts as spending today. What is on credit becomes a debt; paying it later never counts again.',
    hintIncome:
      'The whole total counts as income today. What is on credit is owed to you; collecting it later never counts again.',
    item: 'On credit',
    due: 'Due {{date}}',
    correctTitle: 'Correct',
    reverseOnly: 'Reverse it only',
    correctHint:
      'Change what is wrong and save. The old one is reversed and the credit is adjusted; payments already made stay.',
    payIn: 'Pay in',
    receiveIn: 'Received in',
    youPay: 'You pay {{amount}}',
    youGet: 'You get {{amount}}',
    atRate: 'at 1 {{base}} = {{rate}} {{quote}}, the family rate on that day',
    errors: {
      paidNow: 'Enter what was paid now (0 if nothing)',
      tooMuch: 'Paid now cannot be more than the total',
      person: 'Pick who the rest is owed to or by',
    },
  },
  foreign: {
    toggle: 'Several payments or currencies',
    toggleHint_expense:
      'Pay one bill with several payments, each with its own amount, currency and account.',
    toggleHint_income:
      'Receive one amount in several payments, each with its own amount, currency and account.',
    intro:
      'For example a bill of 45 USD, paid with 20 USD and 2,000,000 LBP. Only the bill counts as spending.',
    bill: 'Bill',
    billHint: 'Optional',
    paid_expense: 'Paid now',
    paid_income: 'Received now',
    returned_expense: 'Returned',
    returned_income: 'Given back',
    returnedHint: 'Change, if any',
    payments: 'Payments',
    returns: 'Change returned',
    addPayment: 'Add payment',
    addReturned: 'Add returned',
    removePayment: 'Remove payment {{n}}',
    removeReturned: 'Remove returned {{n}}',
    totalPaid_expense: 'Total paid, at the family rate',
    totalPaid_income: 'Total received, at the family rate',
    remaining: 'Remaining',
    excess: 'Excess',
    settled: 'Matches the bill',
    summaryHint: 'Estimated with today’s family rate; the saved amounts use the rate of the date.',
    excessConfirm: 'That is {{amount}} more than the bill, and nothing was returned.',
    excessConfirmHint: 'Save it like this, or add what was returned.',
    saveAnyway: 'Save anyway',
    creditHint_expense: 'Whatever the payments leave unpaid of the bill is owed to the shop.',
    creditHint_income: 'Whatever was not received of the amount is owed to you.',
    spent_expense: 'Spent',
    spent_income: 'Earned',
    estimated: 'Estimated with family rate',
    shopRate: 'Shop rate: 1 {{base}} = {{rate}} {{quote}}',
    enterRate: 'Enter a rate for this one',
    errors: {
      rate_required:
        'There is no family rate for {{currency}} yet. Fill in the bill or enter a rate.',
      foreign_expense_nothing_spent: 'The change is as much as what was paid.',
      foreign_expense_amounts_mismatch:
        'Bill, paid and returned do not fit together. Check the amounts and currencies.',
      foreign_expense_too_many_currencies: 'Use at most two currencies.',
      billForCredit: 'Fill in the bill to put the rest on credit.',
    },
    splitOf: 'Split the {{amount}}',
    addedCurrency: '{{currency}} will be added to {{account}}.',
  },
  filters: {
    search: 'Search notes and payees',
    kind: 'Kind',
    kinds: {
      all: 'All',
      expense: 'Expenses',
      income: 'Income',
      transfer: 'Transfers',
      exchange: 'Exchanges',
      debt: 'Debts',
    },
    more: 'More filters',
    moreCount: 'More filters ({{count}})',
    clear: 'Clear filters',
    none: 'Nothing matches these filters.',
    accountId: 'Account',
    categoryId: 'Category',
    payeeId: 'Payee',
    memberId: 'Recorded by',
    from: 'From',
    to: 'To',
    any: 'Any',
    chosen: 'Chosen',
    remove: 'Remove {{name}} filter',
    noOptions: 'Nothing to choose',
  },
  payees: {
    title: 'Payees',
    hint: 'Shops and people money goes to or comes from. Tap one to see its transactions.',
    search: 'Search payees',
    empty: 'No payees yet. They are added when you record an expense or income.',
    uses: '{{count}} uses this year',
    archived: 'Archived',
    seeTransactions: 'See its transactions',
    name: 'Name',
    rename: 'Rename',
    defaultCategory: 'Category to suggest',
    defaultCategoryHint: 'Picked automatically when someone chooses this payee.',
    clearCategory: 'Stop suggesting a category',
    merge: 'Merge into another payee',
    mergeHint: 'For duplicates. Its transactions then show the other name. This cannot be undone.',
    pickMerge: 'Merge into',
    confirmMerge: 'Merge {{from}} into {{into}}?',
    confirm: 'Yes, merge',
    cancel: 'Cancel',
    archive: 'Archive',
    unarchive: 'Bring back',
  },
  activity: {
    title: 'Activity',
    empty: 'Nothing recorded yet.',
    today: 'Today',
    yesterday: 'Yesterday',
    loadMore: 'Show older',
    hiddenAccount: 'Hidden account',
    fromTo: '{{from}} to {{to}}',
    sharedWithFamily: 'Shared with family',
    kinds: {
      expense: 'Expense',
      income: 'Income',
      transfer: 'Transfer',
      opening_balance: 'Opening balance',
      reversal: 'Correction',
      borrow: 'Borrowed',
      lend: 'Lent',
      repay: 'Repaid',
      collect: 'Collected',
      debt_opening: 'Existing debt',
      exchange: 'Exchange',
    },
    corrected: 'Corrected',
  },
  txDetail: {
    title: 'Transaction',
    date: 'Date',
    recordedBy: 'Recorded by',
    kind: 'Kind',
    lines: 'Lines',
    rate: 'Rate',
    dealRate: 'Exchange rate',
    shopRate: 'Shop rate',
    familyRate: 'Family rate that day',
    interest: 'Interest & fees',
    correctionOf: 'This corrects an earlier transaction.',
    seeOriginal: 'See the original',
    corrected: 'This transaction was corrected.',
    seeCorrection: 'See the correction',
    correct: 'Correct',
    correctHint:
      'Correct cancels this transaction with a matching correction, then you record the right one.',
    confirmCorrect: 'Cancel this transaction with a correction?',
    confirmCorrectHint: 'Every balance and total goes back to what it was before it.',
    confirm: 'Yes, correct it',
    cancel: 'Keep it',
    reversed: 'Corrected. Now record the right one.',
    recordAgain: 'Record the right one',
    hiddenLine: 'Hidden account',
  },
  debts: {
    title: 'Debts',
    entry: 'Debts',
    entryHint: 'What we owe and what is owed to us',
    add: 'New debt',
    weOwe: 'We owe',
    owedToUs: 'Owed to us',
    withinFamily: 'Within the family',
    withinFamilyHint: 'These cancel out in the family totals.',
    settled: 'Settled',
    noneOpen: 'Nothing here.',
    empty: 'No debts yet. Record money you borrowed or lent.',
    people: 'People',
    totalsOutside: 'Totals count people outside the family only.',
    privateDebt: 'Private',
    labels: {
      borrowed: 'Borrowed',
      lent: 'Lent',
      repaid: 'Repaid',
      collected: 'Collected',
      existing_owed: 'Existing debt (we owe)',
      existing_owing: 'Existing debt (owed to us)',
    },
    owes: '{{owner}} owes {{person}}',
    owesOwner: '{{person}} owes {{owner}}',
    rowBorrowed: '{{owner}} borrowed',
    rowLent: '{{owner}} lent',
    rowBoughtOnCredit: '{{owner}} bought on credit',
    rowSoldOnCredit: '{{owner}} sold on credit',
    sectionBorrowed: 'Borrowed',
    sectionLent: 'Lent',
    sectionOnCredit: 'On credit',
    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
    payShort: 'Pay',
    collectShort: 'Collect',
    newTitle: 'New debt',
    direction: 'What happened',
    borrowed: 'I borrowed',
    lent: 'I lent',
    whose: 'Whose debt',
    me: 'Me',
    person: 'Person',
    pickPerson: 'Choose person',
    searchPeople: 'Search or type a new name',
    outside: 'People',
    familyMembers: 'Family members',
    newPerson: 'Add "{{name}}"',
    accountFrom: 'Paid from',
    accountTo: 'Money went to',
    theirAccount: "{{name}}'s account",
    existing: 'It started before we used the app',
    existingHint:
      'Enter what is still owed today. No account changes: that money is already in your opening balances.',
    startedOn: 'Started on',
    private: 'Private: only I can see this debt',
    save: 'Save debt',
    outstanding: 'Outstanding',
    of: 'of {{amount}}',
    paidSoFar: 'Paid back {{amount}}',
    history: 'History',
    pay: 'Record repayment',
    collect: 'Record money received',
    payTitle: 'Repay',
    collectTitle: 'Money received',
    payAmount: 'Amount off the debt',
    interest: 'Interest & fees',
    interestHint: 'Optional. Only this part counts as spending.',
    totalPaid: 'Total paid: {{amount}}',
    maxHint: 'At most {{amount}}',
    payAll: 'All of it',
    savePayment: 'Save payment',
    sideOf: "{{name}}'s side",
    seeOtherSide: 'See the other side',
    note: 'Note',
    errors: {
      person: 'Choose a person',
      account: 'Choose an account',
      theirAccount: "Choose the other member's account",
      tooMuch: 'This is more than what is outstanding',
      day: 'Pick a day from 1 to 31',
      month: 'Pick the month of the first installment',
      count: 'Enter how many installments (1 to 600)',
    },
    overdueBadge_one: '{{count}} overdue',
    overdueBadge_other: '{{count}} overdue',
    upcoming: 'Upcoming installments',
    installments: 'Installments',
    planLine: '{{amount}} every month on day {{day}}',
    planCount_one: '{{count}} installment left',
    planCount_other: '{{count}} installments left',
    untilPaid: 'Until paid',
    planFinished: 'All installments are done.',
    setUpPlan: 'Set up installments',
    changePlan: 'Change plan',
    payInstallment: 'Pay installment {{number}}',
    collectInstallment: 'Record installment {{number}}',
    installmentNumber: 'Installment {{number}}',
    installmentOf: 'Installment {{number}} · due {{date}}',
    skip: 'Skip',
    skipConfirm:
      'Skip installment {{number}} due {{date}}? No money moves. A plan with a fixed number of installments gets one more month at the end.',
    skipAria: 'Skip installment {{number}}',
    states: {
      paid: 'Paid',
      skipped: 'Skipped',
      overdue: 'Overdue',
      due_today: 'Due today',
      upcoming: 'Upcoming',
    },
    planTitle: 'Installments',
    planChangeHint:
      'Changes apply from the next installment that is not paid or skipped. Past installments keep their terms.',
    planAmount: 'Amount each month',
    planAmountHint: 'The part that lowers the debt. Interest or fees are added when you pay.',
    dayOfMonth: 'Day of the month',
    dayOfMonthHint: 'In shorter months it falls on the last day (for example 28 or 29 February).',
    startMonth: 'First installment',
    howLong: 'How long',
    fixedCount: 'Fixed number',
    count: 'Installments',
    savePlan: 'Save plan',
    existingPlanHint:
      'Already paying it? Start from the next unpaid installment and enter how many are left.',
  },
  people: {
    title: 'People',
    hint: 'People outside the family you borrow from or lend to.',
    add: 'Add person',
    name: 'Name',
    phone: 'Phone',
    note: 'Note',
    optional: 'Optional',
    search: 'Search people',
    empty: 'Nobody yet. People appear here when you record a debt with them.',
    debts_one: '{{count}} debt',
    debts_other: '{{count}} debts',
    noDebts: 'Nothing owed',
    weOwe: 'We owe',
    owedToUs: 'Owes us',
    edit: 'Edit',
    save: 'Save',
    archive: 'Archive',
    unarchive: 'Bring back',
    archived: 'Archived',
    merge: 'Merge into another person',
    mergeHint: 'Their debts move to the person you pick, and this name is archived.',
    pickMerge: 'Merge into',
    confirmMerge: 'Merge {{from}} into {{into}}?',
    confirm: 'Merge',
    newDebt: 'New debt with {{name}}',
    showArchived: 'Show archived people',
  },
  currency: {
    lockWarning:
      'Your family currency is {{currency}}. It cannot be changed after the first transaction, exchange rate or account in another currency.',
    locked: 'Locked: the family already has transactions, rates or accounts in another currency.',
  },
  accountKinds: {
    cash: 'Cash',
    bank: 'Bank account',
    wallet: 'Wallet',
    card: 'Credit card',
  },
  accountKindHints: {
    wallet: 'Prepaid cards and e-wallets such as Whish Money',
    card: 'Money you owe the bank',
  },
  accounts: {
    title: 'Accounts',
    add: 'New account',
    total: 'Total',
    sharedTotal: 'Shared family',
    mine: 'My accounts',
    shared: 'Shared family accounts',
    ofMember: "{{name}}'s accounts",
    archived: 'Archived',
    showArchived: 'Show archived accounts',
    hideArchived: 'Hide archived accounts',
    empty: 'No accounts yet.',
    emptyHint: 'Add your cash, bank accounts, wallets and cards to start.',
    private: 'Private',
    privateHint: 'Only you can see it',
    owes: 'Owes {{amount}}',
  },
  accountForm: {
    newTitle: 'New account',
    name: 'Name',
    namePlaceholder: 'e.g. My cash, Bank Audi, Visa',
    kind: 'Kind',
    owner: 'Whose account',
    ownerMe: 'Mine',
    ownerShared: 'Shared family account',
    openingBalance: 'Money in it now',
    openingBalanceCard: 'Amount you owe on it now',
    openingBalanceHint: 'Optional. Recorded as an opening balance.',
    invalidAmount: 'Enter an amount like 125.50',
    currency: 'Currency',
    currencyHint: 'You can add the other currency to this account later.',
    private: 'Private: only I can see this account',
    privateHint: 'Your owner allowed you private accounts. Nobody else will see it.',
    submit: 'Create account',
  },
  accountDetail: {
    balance: 'Balance',
    owner: 'Belongs to',
    shared: 'Shared family account',
    rename: 'Name',
    privacy: 'Privacy',
    makePrivate: 'Make private',
    makeVisible: 'Make visible to parents',
    archive: 'Archive account',
    unarchive: 'Unarchive account',
    archiveConfirm:
      'This account still has {{amount}} in it. Archive it anyway? The money still counts in totals.',
    archivedOn: 'Archived',
    archiveHint: 'Archived accounts are hidden from lists but never deleted.',
    currencies: 'Currencies',
    currenciesHint:
      'One account can hold US dollars and Lebanese pounds, each with its own balance.',
    addCurrency: 'Add a currency',
    addCurrencySubmit: 'Add {{currency}}',
    removeCurrency: 'Remove {{currency}}',
    removeConfirm: 'Remove {{currency}} from this account? Its history stays.',
    removeNeedsZero: 'A currency can be removed only when its balance is zero.',
  },
  currencies: {
    option: '{{code}} · {{name}}',
  },
  amounts: {
    fastEntryHint: 'Optional. Whole numbers. You can type 500k or 1.5m.',
    wholeOnly: '{{currency}} amounts are whole numbers, like 500,000 or 500k',
  },
  rates: {
    title: 'Exchange rates',
    intro:
      'The family rate converts Lebanese pounds to US dollars. Each transaction keeps the rate of its own day, so a new rate never changes the past.',
    current: "Today's rate for {{quote}}",
    equation: '1 {{base}} = {{rate}} {{quote}}',
    since: 'Since {{date}}',
    none: 'No rate yet',
    history: 'History',
    historyEmpty: 'No rates yet.',
    by: 'by {{name}}',
    loadMore: 'Show older rates',
    addTitle: 'Add a rate',
    currency: 'Currency',
    rateLabel: 'How many {{quote}} for 1 {{base}}',
    rateHint: 'For example 89,500 means 1 {{base}} = 89,500 {{quote}}.',
    invalidRate: 'Enter a rate like 89,500',
    date: 'Starts on',
    dateHint: 'Today, or an earlier day if you noted the rate before.',
    label: 'Note',
    labelHint: 'Optional, for example "Exchange shop" or "Sayrafa".',
    save: 'Save rate',
    cannotManage: 'Only the owner and parents can add exchange rates.',
    update: 'Update rate',
    staleWarning: 'The {{quote}} rate is more than {{days}} days old.',
    noRateWarning: 'There is no {{quote}} rate yet.',
    atTodaysRate: "at today's rate",
    estimateNote: "Estimate: other currencies are converted at today's rate.",
    missingForEstimate: 'No rate yet for {{currencies}}, so it is not counted here.',
    oneOffLabel: 'Rate for this amount: 1 {{base}} = ? {{quote}}',
    oneOffHint: 'There is no family rate yet, so enter the rate to use for this amount only.',
    usesFamilyRate: 'Uses the family rate 1 {{base}} = {{rate}} {{quote}}.',
  },
  categories: {
    title: 'Categories',
    expense: 'Expenses',
    income: 'Income',
    search: 'Search categories',
    add: 'Add category',
    newName: 'New category name',
    rename: 'Rename',
    archive: 'Archive',
    unarchive: 'Unarchive',
    archived: 'Archived',
    showArchived: 'Show archived',
    none: 'No categories match.',
    readOnly: 'Only the owner or a parent can change categories.',
    used: 'Used {{count}} times',
    parent: 'Inside',
    topLevel: 'Nothing (main category)',
    under: 'In {{name}}',
  },
  familyHub: {
    title: 'Family',
    members: 'Members',
    membersHint: 'People in the family, roles and permissions',
    categories: 'Categories',
    categoriesHint: 'Income and expense categories',
    rates: 'Exchange rates',
    ratesHint: 'The family rate between USD and LBP',
    settings: 'Family settings',
    settingsHint: 'Name, currency and time zone',
    payees: 'Payees',
    payeesHint: 'Shops and people, merge duplicates',
    account: 'My account',
    accountHint: 'Password, appearance, sign out, delete account',
    budgets: 'Budgets',
    budgetsHint: 'Monthly household and category budgets',
    goals: 'Savings goals',
    goalsHint: 'What the family is saving for',
    reports: 'Reports',
    reportsHint: 'Where the money went, month by month, debts',
  },
  members: {
    title: 'Members',
    add: 'Add member',
    you: 'You',
    canSignIn: 'Can sign in',
    noLogin: 'No login',
    deactivated: 'Deactivated',
    pendingTitle: 'Waiting to join',
    pendingLine: 'Invited as {{email}} · expires {{date}}',
    cancelInvite: 'Cancel invitation',
    privateAllowed: 'Private accounts allowed',
    loadMore: 'Show more',
  },
  addMember: {
    title: 'Add a member',
    intro: 'Add someone who does not sign in (like a young child), or invite them to sign in.',
    inviteToggle: 'Invite them to sign in',
    inviteEmail: 'Their email',
    submit: 'Add member',
    submitInvite: 'Add and invite',
  },
  memberDetail: {
    edit: 'Details',
    status: 'Status',
    active: 'Active',
    deactivated: 'Deactivated',
    deactivate: 'Deactivate',
    deactivateConfirm:
      'Deactivate {{name}}? They will be signed out and cannot sign in until reactivated.',
    reactivate: 'Reactivate',
    invite: 'Invite to sign in',
    permissionsTitle: 'Permissions',
    permissionsIntro: 'Start from the role, then allow or block single permissions.',
    permissionDefault: 'Role default ({{value}})',
    allow: 'Allowed',
    deny: 'Blocked',
    yes: 'yes',
    no: 'no',
    savePermissions: 'Save permissions',
  },
  invite: {
    title: 'Invite {{name}}',
    intro:
      'We will make a link for {{name}}. Send it to them; it works once and expires in 7 days.',
    email: 'Their email',
    submit: 'Create invitation link',
    linkTitle: 'Invitation link',
    linkIntro: 'Send this link to {{name}}. It is shown only once.',
    shareText: 'Join our family on FamilyWallet',
    done: 'Done',
  },
  family: {
    title: 'Family settings',
    readOnly: 'Only the owner or a parent can change these.',
  },
  account: {
    title: 'Account',
    signedInAs: 'Signed in as {{email}}',
    profile: 'Your name',
    theme: 'Appearance',
    themeSystem: 'Same as device',
    themeLight: 'Light',
    themeDark: 'Dark',
    passwordTitle: 'Change password',
    passwordCurrent: 'Current password',
    passwordNew: 'New password',
    passwordConfirm: 'Type the new password again',
    passwordSubmit: 'Change password',
    passwordHint: 'Your other phones and computers are signed out. This one stays signed in.',
    passwordChanged: 'Password changed. Other devices were signed out.',
    sessions: 'Sessions',
    logout: 'Sign out',
    logoutAll: 'Sign out everywhere',
    logoutAllHint: 'Signs out on every phone and computer, including this one.',
    deleteTitle: 'Delete account',
    deleteIntro:
      'Your login is removed and you are signed out everywhere. You stay in the family as a member without a login.',
    deleteIntroOwner:
      'As the owner, deleting your account deletes the whole family, including every account and transaction. This is only possible when nobody else can sign in.',
    deleteConfirm: 'Enter your password to confirm',
    deleteFamilyName: 'Type the family name "{{name}}" to confirm',
    deleteSubmit: 'Delete my account',
  },
  status: {
    heading: 'System status',
    checking: 'Checking the server…',
    ok: 'Everything is running',
    error: 'The server is not reachable right now',
    version: 'Version {{version}}',
  },
  recurring: {
    title: 'Recurring',
    entry: 'Recurring',
    entryHint: 'Salary, bills, allowance',
    empty: 'Nothing repeats yet. Add the salary, a bill or an allowance.',
    add: 'Add recurring',
    newTitle: 'New recurring',
    editTitle: 'Edit recurring',
    name: 'Name',
    namePlaceholder: 'Generator, internet, salary…',
    amount: 'Amount',
    amountOptional: 'Leave empty when it changes every time',
    account: 'Account',
    fromAccount: 'From',
    toAccount: 'To',
    category: 'Category',
    payee: 'Payee',
    note: 'Note',
    repeats: 'Repeats',
    monthly: 'Monthly',
    weekly: 'Weekly',
    dayOfMonth: 'Day of the month',
    dayOfMonthHint: 'In shorter months it falls on the last day.',
    weekday: 'Day of the week',
    startOn: 'Starts',
    endOn: 'Ends',
    endHint: 'Leave the end empty to repeat until you stop it.',
    autoRecord: 'Record automatically',
    autoRecordHint:
      'Only for a fixed amount. It is recorded on the due date; if that is not possible it shows as due instead.',
    save: 'Save',
    changeHint:
      'Changes apply from today on. Occurrences already recorded or skipped stay as they are.',
    pause: 'Pause',
    resume: 'Resume',
    paused: 'Paused',
    ended: 'Ended',
    delete: 'Delete',
    deleteConfirm: 'Delete "{{name}}"? Transactions already recorded from it stay.',
    deleteYes: 'Yes, delete it',
    keep: 'Keep it',
    next: 'Next: {{date}}',
    everyMonth: 'Every month on day {{day}}',
    everyWeek: 'Every {{weekday}}',
    auto: 'Automatic',
    record: 'Record',
    skip: 'Skip',
    skipConfirm: 'Skip "{{name}}" due {{date}}? Nothing is recorded.',
    skipOlder: 'Skip all older',
    skipOlderConfirm: 'Skip the {{count}} older occurrences of "{{name}}"? Nothing is recorded.',
    olderCount: '{{count}} missed',
    recordingFor: 'Recording "{{name}}" · {{date}}. Change the amount if it is different.',
    recordedAutomatically: 'Recorded automatically',
    fromRecurring: 'From "{{name}}"',
    lateNotice_one: '{{count}} occurrence was recorded late',
    lateNotice_other: '{{count}} occurrences were recorded late',
    lateNoticeHint: 'The app was not running on the due date; they are dated on their due dates.',
    dismiss: 'Dismiss',
    errors: {
      name: 'Give it a name',
      autoNeedsAmount: 'Recording automatically needs a fixed amount',
      account: 'Pick an account',
      toAccount: 'Pick another account',
      category: 'Pick a category',
      date: 'Pick a date',
      end: 'The end must be on or after the start',
    },
    weekdays: {
      '0': 'Sunday',
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
    },
  },
  reports: {
    title: 'Reports',
    mineTitle: 'My spending',
    whose: 'Whose spending',
    pdf: { export: 'Export PDF', exporting: 'Preparing…' },
    home: {
      title: 'Spent so far this month',
      see: 'Reports',
      nothing: 'No spending yet this month or the same days of last month.',
      more: '{{amount}} more than the same days last month{{percent}}',
      less: '{{amount}} less than the same days last month{{percent}}',
      same: 'Same as the same days last month',
      top: 'Most on {{name}}: {{amount}}',
    },
    wholeFamily: 'Whole family',
    percent: '{{percent}}%',
    countsHint:
      'Amounts are in {{currency}}, at the rate used when each was recorded. Credit purchases count in full on their day. Transfers, exchanges, debts and opening balances do not count; only debt interest does.',
    where: {
      title: 'Where the money went',
      total: 'Spent',
      empty: 'No spending in this period.',
      period: 'Period',
      month: 'Month',
      range: 'Custom range',
      from: 'From',
      to: 'To',
    },
    compare: {
      title: 'This month vs last month',
      periods: '{{current}} vs {{previous}}',
      now: 'This period',
      before: 'Last month',
      fromTo: '{{before}} → {{now}}',
      more: 'more',
      less: 'less',
      same: 'no change',
      fairHint: 'Compared with the same days of last month.',
    },
    income: {
      title: 'Income vs expense',
      twelveMonths: 'Last 12 months',
      saved: 'Saved {{amount}}',
      overspent: 'Overspent {{amount}}',
      exchangeDifference: 'Exchange difference {{amount}} (not income or expense)',
    },
    debts: {
      title: 'Debts',
      owedToUs: 'Owed to us',
      weOwe: 'We owe',
      none: 'Nothing open.',
      total: 'Total',
      atTodaysRate: '{{amount}} at today’s rate',
      credit: 'On credit',
      installment: 'Installment',
      state: { overdue: 'Overdue', due_today: 'Due today', upcoming: 'Due soon' },
    },
  },
  budgets: {
    title: 'Budgets',
    homeTitle: 'Budget this month',
    overall: 'Household budget',
    overallHint: 'Every expense counts, with or without a category budget',
    categories: 'Category budgets',
    unbudgeted: 'No budget yet',
    spentOf: '{{spent}} of {{amount}}',
    spentOnly: '{{spent}} spent',
    left: '{{amount}} left',
    over: '{{amount}} over budget',
    warning: 'Almost used',
    overBadge: 'Over budget',
    categoriesOver_one: '{{count}} category over budget',
    categoriesOver_other: '{{count}} categories over budget',
    noBudget: 'No budget set',
    set: 'Set budget',
    add: 'Add budget',
    edit: 'Change',
    empty: 'No budgets yet. Set a monthly household budget to see how spending is going.',
    readOnly: 'Only the owner and parents can change budgets.',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    pastMonth: 'Past months keep the budgets they had.',
    countsHint:
      'Amounts are in {{currency}}. Other currencies count at the rate used when they were recorded. Transfers, exchanges and debts do not count; only debt interest does.',
    formTitle: 'Monthly budget',
    category: 'Category',
    pickCategory: 'Pick a category',
    amount: 'Amount each month ({{currency}})',
    fromThisMonth: 'Applies from this month on. Past months do not change.',
    save: 'Save budget',
    remove: 'Remove budget',
    errors: {
      amount: 'Enter an amount of zero or more.',
      category: 'Pick a category.',
    },
  },
  limits: {
    title: 'Monthly limit',
    homeTitle: 'Left this month',
    childrenTitle: "Children's limits",
    of: 'of {{amount}} this month',
    spent: '{{amount}} spent',
    over: 'Over limit',
    overBy: '{{amount}} over the limit',
    none: 'No monthly limit',
    hint: 'Counts expenses {{name}} records or pays from their own accounts, in {{currency}}. Going over is still recorded.',
    amount: 'Limit each month ({{currency}})',
    save: 'Save limit',
    remove: 'Remove limit',
    saved: 'Limit saved.',
    warnTitle: 'This goes over your monthly limit',
    warnLeft:
      'You have {{amount}} left this month. You can still save it; your parents will see it.',
    warnAlreadyOver:
      'You are already over your monthly limit. You can still save it; your parents will see it.',
  },
  goals: {
    title: 'Savings goals',
    homeTitle: 'Goals',
    add: 'New goal',
    newTitle: 'New goal',
    editTitle: 'Change goal',
    empty: 'No savings goals yet. Pick an account and a target to track your progress.',
    saved: '{{saved}} of {{target}}',
    remaining: '{{amount}} to go',
    reached: 'Reached',
    late: 'Target date passed',
    perMonth: 'Put aside {{amount}} a month',
    by: 'By {{date}}',
    family: 'Family',
    personal: 'Personal · {{name}}',
    archived: 'Archived',
    progress: 'Progress',
    account: 'Account',
    pickAccount: 'Pick an account',
    name: 'Name',
    namePlaceholder: 'New car, holiday, school fees',
    target: 'Target',
    currency: 'Currency',
    targetDate: 'Target date',
    targetDateHint: 'Optional. With a date you see how much to put aside each month.',
    familyWide: 'Family goal',
    familyWideHint: 'Everyone who can see the account sees it. Owner and parents can change it.',
    progressHint: 'Progress is the balance of {{account}} in {{currency}}.',
    save: 'Save goal',
    archive: 'Archive goal',
    unarchive: 'Bring back',
    edit: 'Change goal',
    showArchived: 'Show archived goals',
    hideArchived: 'Hide archived goals',
    noAccounts: 'Goals are linked to a cash, bank or wallet account. Add one first.',
    errors: {
      name: 'Give the goal a name.',
      account: 'Pick an account.',
      date: 'Pick a valid date.',
    },
  },
  notFound: {
    title: 'Page not found',
    home: 'Go home',
  },
} as const;
